import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppSelector } from 'store';
import { selectIsDropshipSetup } from 'store/selectors/me/company';
import Menu from 'storybookConfig/stories/cells/Menu';
import Badge from 'storybookConfig/stories/molecules/Badge';
import { listTradeOpsIssues } from 'utils/api/issues';
import { useAnnouncementToDisplay } from './Announcements';
import Body from 'storybookConfig/stories/molecules/Body';
import Icon from 'storybookConfig/stories/molecules/Icon';
import useFlagEnabled from 'hooks/useFlagEnabled';

const FETCHING_TRADE_OPS_ISSUES_QUERY_KEY = [
  'listTradeOpsIssues',
  {
    page: 0,
    limit: 100,
    archived: false,
    severityLevels: ['error', 'critical'],
  },
] as const;

interface FooterMenuProps {
  isActive: (path: string) => boolean;
}

const FooterMenu = ({ isActive }: FooterMenuProps) => {
  const isDropshipSetup = useAppSelector(selectIsDropshipSetup);
  const announcementToDisplay = useAnnouncementToDisplay();
  const [expanded, setExpanded] = useState(!announcementToDisplay);
  const showMarketplace = useFlagEnabled('showMarketplace');

  /**
   * Queries
   */

  const fetchingTradeOpsIssues = useQuery({
    queryKey: FETCHING_TRADE_OPS_ISSUES_QUERY_KEY,
    queryFn: ({ queryKey }) => listTradeOpsIssues(queryKey[1]),
    enabled: isDropshipSetup,
  });

  const tradeOpsIssues = fetchingTradeOpsIssues.data?.data?.errors ?? [];

  /**
   * Render
   */

  if (!showMarketplace) {
    // Render the original links directly, no dropdown
    return (
      <Menu>
        {isDropshipSetup && (
          <Menu.Link to="/issues" $iconName="error" $isActive={isActive('/issues')}>
            Issues
            <Badge variant="info" count={tradeOpsIssues.length} data-testid="issue-count-badge" />
          </Menu.Link>
        )}
        <Menu.Link to="/whats-new" $iconName="new_releases" $isActive={isActive('/whats-new')}>
          What&apos;s New
        </Menu.Link>
        <Menu.Link
          to="#"
          as="a"
          $iconName="list_alt"
          href="https://moderndropship.frontkb.com/en"
          rel="noopener noreferrer"
          target="_blank"
        >
          Help Center
        </Menu.Link>
        <Menu.Link
          to="#"
          as="a"
          $iconName="support_agent"
          href="https://moderndropship.frontkb.com/en/contact-us"
          rel="noopener noreferrer"
          target="_blank"
        >
          Submit an Issue
        </Menu.Link>
        <Menu.Link to="/settings" $iconName="settings">
          Settings
        </Menu.Link>
      </Menu>
    );
  }

  // New dropdown UI for flagged users
  return (
    <Menu>
      <>
        <Menu.LinkButton
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Body variant="Body/Header">Settings</Body>
          <Icon name={expanded ? 'arrow_drop_up' : 'arrow_drop_down'} />
        </Menu.LinkButton>
        {expanded && (
          <>
            {isDropshipSetup && (
              <Menu.Link to="/issues" $iconName="error" $isActive={isActive('/issues')}>
                Issues
                <Badge
                  variant="info"
                  count={tradeOpsIssues.length}
                  data-testid="issue-count-badge"
                />
              </Menu.Link>
            )}
            <Menu.Link to="/whats-new" $iconName="new_releases" $isActive={isActive('/whats-new')}>
              What&apos;s New
            </Menu.Link>
            <Menu.Link
              to="#"
              as="a"
              $iconName="list_alt"
              href="https://moderndropship.frontkb.com/en"
              rel="noopener noreferrer"
              target="_blank"
            >
              Help Center
            </Menu.Link>
            <Menu.Link
              to="#"
              as="a"
              $iconName="support_agent"
              href="https://moderndropship.frontkb.com/en/contact-us"
              rel="noopener noreferrer"
              target="_blank"
            >
              Submit an Issue
            </Menu.Link>
            <Menu.Link to="/settings" $iconName="settings">
              Settings
            </Menu.Link>
          </>
        )}
      </>
    </Menu>
  );
};

export default FooterMenu;
